/**
 * Jagdzustand aus Jagdzeitplan und letztem Eingriff (ADR-0017). Reine Funktion, damit Server (Zeitgeber) und Gerät
 * (Ableitung ohne Netz, ADR-0020) dieselbe Regel verwenden.
 */
export type Jagdzustand = "Jagd im Gange" | "Jagd ruht";

export interface Jagdabschnitt {
  Beginn: Date;
  Ende: Date;
}

export type Eingriffsart = "Hahn in Ruh" | "Jagd freigeben" | "Zum Zeitplan zurückkehren";

export interface Eingriff {
  art: Eingriffsart;
  zeitpunkt: Date;
}

/** Darstellung: Hintergrund, Text und Symbol gehören zusammen (F-080, F-081, oberflaeche.md §4.1). */
export const DARSTELLUNG: Record<Jagdzustand, { hintergrund: "grün" | "rot"; text: string; symbol: string; zustand: "gang" | "ruhe" }> = {
  "Jagd im Gange": { hintergrund: "grün", text: "Jagd im Gange", symbol: "jagd-im-gange", zustand: "gang" },
  "Jagd ruht": { hintergrund: "rot", text: "Hahn in Ruh", symbol: "hahn-in-ruh", zustand: "ruhe" },
};

function imAbschnitt(t: Date, a: Jagdabschnitt): boolean {
  return t.getTime() >= a.Beginn.getTime() && t.getTime() < a.Ende.getTime();
}

/** Ende des laufenden oder nächsten Abschnitts nach `t` (für „Jagd freigeben“ hält bis dahin). */
function endeLaufendOderNaechst(t: Date, plan: readonly Jagdabschnitt[]): Date | null {
  const kandidaten = plan.filter((a) => a.Ende.getTime() > t.getTime()).sort((x, y) => x.Beginn.getTime() - y.Beginn.getTime());
  return kandidaten[0]?.Ende ?? null;
}

export function jagdzustandZu(t: Date, plan: readonly Jagdabschnitt[], eingriff: Eingriff | null): Jagdzustand {
  if (eingriff && eingriff.zeitpunkt.getTime() <= t.getTime()) {
    if (eingriff.art === "Hahn in Ruh") return "Jagd ruht";
    if (eingriff.art === "Jagd freigeben") {
      const ende = endeLaufendOderNaechst(eingriff.zeitpunkt, plan);
      if (ende === null || t.getTime() < ende.getTime()) return "Jagd im Gange";
    }
  }
  return plan.some((a) => imAbschnitt(t, a)) ? "Jagd im Gange" : "Jagd ruht";
}

/** Nächster geplanter Wechsel nach `t` (lesemodelle.md §1.5 „Nächster geplanter Wechsel“). */
export function naechsterWechsel(t: Date, plan: readonly Jagdabschnitt[], eingriff: Eingriff | null): { Zeitpunkt: Date; Jagdzustand: Jagdzustand } | null {
  const jetzt = jagdzustandZu(t, plan, eingriff);
  const punkte = plan.flatMap((a) => [a.Beginn, a.Ende]).filter((p) => p.getTime() > t.getTime()).sort((a, b) => a.getTime() - b.getTime());
  for (const p of punkte) {
    const z = jagdzustandZu(p, plan, eingriff);
    if (z !== jetzt) return { Zeitpunkt: p, Jagdzustand: z };
  }
  return null;
}
